import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockProducts } from '../data/mockData';
import { useEcommerce } from '../context/EcommerceContext';
import ShareModal from '../components/Modals/ShareModal';
import GroupBuyModal from '../components/Modals/GroupBuyModal';
import EmbeddableVideo from '../components/Common/EmbeddableVideo';
import { ProductImageGallery } from '../components/Marketplace/ProductImageGallery';
import { StockBanner, getStockState } from '../components/Marketplace/StockBadge';
import { writeFetch } from '../api';
import { getCached, setCached } from '../utils/apiCache';

const {
  FiArrowLeft, FiShoppingBag, FiHeart, FiShare2,
  FiTruck, FiShield, FiCheckCircle, FiUsers,
  FiTarget, FiClock, FiPlus, FiChevronRight,
  FiMessageSquare, FiQuote, FiVideo, FiImage,
  FiRefreshCw, FiAward, FiBell, FiAlertCircle,
} = FiIcons;

const TRUST_BADGES = [
  { icon: FiShield, label: 'Secure Payment', desc: 'SSL encrypted checkout' },
  { icon: FiRefreshCw, label: 'Easy Returns', desc: '30-day hassle-free returns' },
  { icon: FiTruck, label: 'Global Delivery', desc: 'Ships to funeral homes worldwide' },
  { icon: FiAward, label: 'Verified Vendor', desc: 'Authenticity guaranteed' },
];

const TrustBadges = () => (
  <div className="grid grid-cols-2 gap-3">
    {TRUST_BADGES.map(({ icon, label, desc }) => (
      <div key={label} className="flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark">
        <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center text-accent-gold flex-shrink-0 shadow-sm">
          <SafeIcon icon={icon} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy leading-tight">{label}</p>
          <p className="text-[9px] text-text-muted font-light leading-tight mt-0.5">{desc}</p>
        </div>
      </div>
    ))}
  </div>
);

const MediaTabs = ({ activeTab, setActiveTab, hasVideo }) => (
  <div className="flex gap-2 mb-4">
    <button onClick={() => setActiveTab('image')}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
        activeTab === 'image'
          ? 'bg-accent-navy border-accent-navy text-white'
          : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
      }`}>
      <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Gallery
    </button>
    {hasVideo && (
      <button onClick={() => setActiveTab('video')}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
          activeTab === 'video'
            ? 'bg-accent-gold border-accent-gold text-white'
            : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
        }`}>
        <SafeIcon icon={FiVideo} className="w-3.5 h-3.5" /> Demo Video
      </button>
    )}
  </div>
);

const StockAvailabilityRow = ({ stock }) => {
  const state = getStockState(stock);
  if (state === 'normal') return null;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
      <StockBanner stock={stock} />
    </motion.div>
  );
};

const ProductDetailSkeleton = () => (
  <div className="pt-32 min-h-screen bg-brand-light pb-20">
    <div className="container mx-auto px-6">
      <div className="h-4 w-40 bg-brand-light rounded animate-pulse mb-12" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div className="space-y-4">
          <div className="aspect-square bg-brand-light rounded-[40px] animate-pulse" />
          <div className="flex gap-2">
            <div className="w-16 h-16 bg-brand-light rounded-2xl animate-pulse" />
            <div className="w-16 h-16 bg-brand-light rounded-2xl animate-pulse" />
            <div className="w-16 h-16 bg-brand-light rounded-2xl animate-pulse" />
          </div>
        </div>
        <div className="space-y-6">
          <div className="h-6 w-24 bg-brand-light rounded animate-pulse" />
          <div className="h-16 w-3/4 bg-brand-light rounded animate-pulse" />
          <div className="h-4 w-40 bg-brand-light rounded animate-pulse" />
          <div className="h-12 w-32 bg-brand-light rounded animate-pulse" />
          <div className="h-24 w-full bg-brand-light rounded animate-pulse" />
          <div className="h-32 w-full bg-brand-light rounded animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-16 w-full bg-brand-light rounded-2xl animate-pulse" />
            <div className="h-16 w-full bg-brand-light rounded-2xl animate-pulse" />
            <div className="h-16 w-full bg-brand-light rounded-2xl animate-pulse" />
            <div className="h-16 w-full bg-brand-light rounded-2xl animate-pulse" />
          </div>
          <div className="h-14 w-full bg-brand-light rounded-2xl animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, wishlist, toggleWishlist, groupBuys, startGroupBuy, joinGroupBuy } = useEcommerce();

  const [product, setProduct] = useState(null);
  const [variations, setVariations] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isAdded, setIsAdded] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isGroupBuyOpen, setIsGroupBuyOpen] = useState(false);
  const [mediaTab, setMediaTab] = useState('image');
  const [notifyDone, setNotifyDone] = useState(false);

  const productCacheKey = `product_${id}`;
  const variationCacheKey = `variations_${id}`;

  const applyInitialSelections = useCallback((items) => {
    const grouped = items.reduce((groups, variation) => {
      if (!groups[variation.type]) groups[variation.type] = [];
      groups[variation.type].push(variation);
      return groups;
    }, {});

    const initialSelections = {};
    Object.keys(grouped).forEach(type => {
      const availableOption = grouped[type].find(option => option.stock_count > 0) || grouped[type][0];
      initialSelections[type] = availableOption.id;
    });
    setSelectedVariations(initialSelections);
  }, []);

  const fetchProductAndVariations = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    const cachedProduct = getCached(productCacheKey, 60000);
    const cachedVariations = getCached(variationCacheKey, 60000);

    if (cachedProduct !== null) setProduct(cachedProduct);
    if (cachedVariations !== null) {
      setVariations(cachedVariations);
      applyInitialSelections(cachedVariations);
    }

    const productRequest = cachedProduct !== null
      ? Promise.resolve(null)
      : writeFetch(`read/merchant_products_1776540001/${encodeURIComponent(id)}`)
        .then(response => response.json());
    const variationsRequest = cachedVariations !== null
      ? Promise.resolve(null)
      : writeFetch(`read/product_variations_1776540010?product_id=${encodeURIComponent(id)}&limit=100`)
        .then(response => response.json());

    const [productResult, variationResult] = await Promise.allSettled([
      productRequest,
      variationsRequest,
    ]);

    let foundProduct = cachedProduct ?? null;

    if (productResult.status === 'fulfilled' && productResult.value) {
      const response = productResult.value;
      if (response?.status === 'success' && response?.data && !Array.isArray(response.data)) {
        const item = response.data;
        foundProduct = {
          id,
          name: item?.name ?? '',
          price: parseFloat(item?.price) || 0,
          category: item?.category ?? '',
          image: item?.image_url || 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?w=800',
          description: item?.description ?? '',
          stock: item?.stock_count ?? 0,
          vendor: 'Verified Merchant',
          rating: 5.0,
          videoUrl: item?.video_url ?? undefined,
        };
        setProduct(foundProduct);
        setCached(productCacheKey, foundProduct);
      }
    } else if (productResult.status === 'rejected') {
      console.error('Product request failed:', productResult.reason);
    }

    if (!foundProduct) {
      try {
        for (let index = 0; index < localStorage.length; index++) {
          const key = localStorage.key(index);
          if (!key || !key.startsWith('mock_products_')) continue;

          const stored = JSON.parse(localStorage.getItem(key) || '[]');
          const localProduct = Array.isArray(stored)
            ? stored.find(item => String(item?.id) === String(id))
            : null;

          if (localProduct) {
            foundProduct = {
              id: localProduct.id,
              name: localProduct.name ?? '',
              price: parseFloat(localProduct.price) || 0,
              category: localProduct.category ?? '',
              image: localProduct.image_url || 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?w=800',
              description: localProduct.description ?? '',
              stock: localProduct.stock_count ?? 0,
              vendor: 'Verified Merchant',
              rating: 5.0,
              videoUrl: localProduct.video_url ?? undefined,
            };
            break;
          }
        }
      } catch (storageError) {
        console.warn('Unable to read locally stored product', storageError);
      }
    }

    if (!foundProduct) {
      foundProduct = mockProducts.find(item => String(item.id) === String(id)) ?? null;
    }

    if (foundProduct) {
      setProduct(foundProduct);
      setCached(productCacheKey, foundProduct);
    } else {
      setError('Unable to load product details. Please try again.');
    }

    let nextVariations = cachedVariations ?? [];
    if (variationResult.status === 'fulfilled' && variationResult.value) {
      const response = variationResult.value;
      if (response?.status === 'success' && Array.isArray(response?.data)) {
        nextVariations = response.data;
        setVariations(nextVariations);
        setCached(variationCacheKey, nextVariations);
        applyInitialSelections(nextVariations);
      } else if (response?.status === 'error') {
        console.warn('Unable to load product variations:', response?.message ?? 'Request failed');
      }
    } else if (variationResult.status === 'rejected') {
      console.warn('Variation request failed:', variationResult.reason);
    }

    if (foundProduct && productResult.status === 'rejected' && cachedProduct === null) {
      setError('Unable to load product details. Please try again.');
    }

    setLoading(false);
  }, [id, productCacheKey, variationCacheKey, applyInitialSelections]);

  useEffect(() => {
    if (id) fetchProductAndVariations(true);
  }, [id, fetchProductAndVariations]);

  const groupedVariations = useMemo(() => {
    return variations.reduce((groups, variation) => {
      if (!groups[variation.type]) groups[variation.type] = [];
      groups[variation.type].push(variation);
      return groups;
    }, {});
  }, [variations]);

  const variationPriceModifier = useMemo(() => {
    let modifier = 0;
    Object.values(selectedVariations).forEach(variationId => {
      const variation = variations.find(item => item.id === variationId);
      if (variation) modifier += parseFloat(variation.price_modifier) || 0;
    });
    return modifier;
  }, [selectedVariations, variations]);

  const finalPrice = product ? (product.price + variationPriceModifier).toFixed(2) : '0.00';

  const isOutOfStock = useMemo(() => {
    if (!product) return true;
    if (variations.length > 0) {
      for (const variationId of Object.values(selectedVariations)) {
        const variation = variations.find(item => item.id === variationId);
        if (variation && variation.stock_count <= 0) return true;
      }
      return false;
    }
    return product.stock === 0;
  }, [product, variations, selectedVariations]);

  if (loading) return <ProductDetailSkeleton />;

  if (error) {
    return (
      <div className="pt-32 min-h-screen bg-brand-light pb-20 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-accent-navy mb-2">Something went wrong</h1>
          <p className="text-text-muted mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => fetchProductAndVariations(true)}
              className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all flex items-center gap-2">
              <SafeIcon icon={FiRefreshCw} /> Try Again
            </button>
            <button onClick={() => navigate(-1)}
              className="px-6 py-3 bg-white border border-brand-dark text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:border-accent-gold transition-all">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-32 min-h-screen bg-brand-light pb-20 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiShoppingBag} className="w-16 h-16 text-text-muted/30 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-accent-navy mb-2">Product Not Found</h1>
          <p className="text-text-muted mb-6">The product you're looking for doesn't exist or may have been removed.</p>
          <button onClick={() => navigate(-1)}
            className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isWishlisted = wishlist.some(item => item.id === product.id);
  const hasVideo = !!product.videoUrl;
  const queryParams = new URLSearchParams(location.search);
  const activeGbId = queryParams.get('gb');
  const activeGroupBuys = groupBuys.filter(groupBuy => groupBuy.productId === product.id && groupBuy.status === 'active');
  const highlightedGb = activeGroupBuys.find(groupBuy => groupBuy.id === activeGbId);

  const handleAddToCart = () => {
    const selectedOptions = Object.values(selectedVariations).map(variationId => {
      const variation = variations.find(item => item.id === variationId);
      return variation ? `${variation.type}: ${variation.value}` : null;
    }).filter(Boolean);

    addToCart({
      ...product,
      price: parseFloat(finalPrice),
      selectedVariations: selectedOptions
    });
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleStartGroupBuy = (goal, deadline, description) => startGroupBuy(product, goal, deadline, description);
  const handleNotifyMe = () => {
    setNotifyDone(true);
    setTimeout(() => setNotifyDone(false), 3000);
  };
  const calculateProgress = (current, goal) => Math.min((current / goal) * 100, 100);
  const getTimeRemaining = deadline => {
    const days = Math.floor((Date.parse(deadline) - Date.now()) / 86400000);
    return days > 0 ? `${days} days left` : 'Last day!';
  };
  const calculatePortion = (price, goal) => (price / goal).toFixed(2);

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6">
        <div className="mb-12">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors">
            <SafeIcon icon={FiArrowLeft} /> Back to Marketplace
          </button>
        </div>

        <AnimatePresence>
          {highlightedGb && (
            <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }}
              className="mb-16 bg-white border-2 border-accent-gold rounded-[48px] overflow-hidden shadow-2xl shadow-accent-gold/10">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3 bg-accent-gold p-10 flex flex-col justify-between text-white relative overflow-hidden">
                  <SafeIcon icon={FiQuote} className="absolute -top-4 -left-4 w-32 h-32 opacity-10 rotate-12" />
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-white/20 rounded-3xl flex items-center justify-center mb-6">
                      <SafeIcon icon={FiUsers} className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl font-serif mb-4 leading-tight">Collective Legacy Campaign</h2>
                    <p className="text-white/80 text-sm font-light leading-relaxed">
                      Join this initiative to purchase this tribute together and preserve the memory through shared effort.
                    </p>
                  </div>
                  <div className="mt-8 pt-8 border-t border-white/20">
                    <div className="flex items-center gap-3 mb-2">
                      <SafeIcon icon={FiTarget} className="text-white" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Goal: {highlightedGb.goal} Units</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiClock} className="text-white" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Ends In: {getTimeRemaining(highlightedGb.deadline)}</span>
                    </div>
                  </div>
                </div>
                <div className="lg:w-2/3 p-10 md:p-14 flex flex-col">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-full bg-accent-gold/10 flex items-center justify-center text-accent-gold">
                      <SafeIcon icon={FiMessageSquare} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy/60">A Message from the Creator</span>
                  </div>
                  <blockquote className="relative mb-10">
                    <p className="text-2xl md:text-3xl font-serif text-accent-navy leading-relaxed italic">
                      "{highlightedGb.description || "I'm starting this group buy so we can collectively contribute to this beautiful tribute."}"
                    </p>
                  </blockquote>
                  <div className="mt-auto pt-10 border-t border-brand-dark flex flex-col md:flex-row items-center justify-between gap-8">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">Your Collective Portion</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-serif text-accent-navy">${calculatePortion(finalPrice, highlightedGb.goal)}</span>
                        <span className="text-sm text-text-muted">/ contribution</span>
                      </div>
                    </div>
                    <button onClick={() => joinGroupBuy(highlightedGb.id)}
                      className="w-full md:w-auto px-12 py-6 bg-accent-navy text-white rounded-[24px] text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3">
                      Accept Invite & Join <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            {hasVideo && <MediaTabs activeTab={mediaTab} setActiveTab={setMediaTab} hasVideo={hasVideo} />}

            <AnimatePresence mode="wait">
              {mediaTab === 'image' && (
                <motion.div key="gallery"
                  initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.25 }}>
                  <div className="relative">
                    <ProductImageGallery product={product} />
                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] rounded-[40px] flex items-center justify-center z-10 pointer-events-none">
                        <div className="bg-zinc-800/80 text-white px-6 py-3 rounded-2xl text-sm font-black uppercase tracking-widest backdrop-blur-sm">
                          Currently Unavailable
                        </div>
                      </div>
                    )}
                  </div>

                  {activeGbId && (
                    <div className="mt-4 flex items-center justify-center">
                      <div className="bg-accent-gold text-white px-6 py-3 rounded-2xl shadow-xl font-bold flex items-center gap-3 border-2 border-white/20">
                        <SafeIcon icon={FiUsers} /> Active Invite
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {mediaTab === 'video' && (
                <motion.div key="video"
                  initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.25 }}
                  className="rounded-[40px] overflow-hidden border border-brand-dark shadow-2xl bg-black">
                  <EmbeddableVideo
                    url={product.videoUrl}
                    title={`${product.name} — Product Demo`}
                    description={`See ${product.name} up close — craftsmanship, packaging and delivery experience.`}
                    aspectRatio="16:9" useThumbnail={true} lightbox={true}
                    allowFullscreen={true} compact={false} className="rounded-[40px]" />
                </motion.div>
              )}
            </AnimatePresence>

            {hasVideo && mediaTab === 'image' && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
                <button onClick={() => setMediaTab('video')}
                  className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl border border-brand-dark hover:border-accent-gold transition-all group shadow-sm">
                  <div className="w-20 h-14 rounded-xl overflow-hidden bg-black flex-shrink-0 relative">
                    <img src={product.image} alt="video preview" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-70" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-7 h-7 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                        <SafeIcon icon={FiVideo} className="w-3 h-3 text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-accent-navy group-hover:text-accent-gold transition-colors">Watch Product Demo</p>
                    <p className="text-[10px] text-text-muted font-light">See craftsmanship, packaging & delivery experience</p>
                  </div>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-text-muted group-hover:text-accent-gold transition-colors flex-shrink-0" />
                </button>
              </motion.div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <div className="mb-6 text-center lg:text-left">
              <span className="bg-accent-gold/10 text-accent-gold px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-accent-gold/20 mb-6 inline-block">
                {product.category}
              </span>
              <h1 className="text-4xl md:text-6xl font-serif text-accent-navy mb-4">{product.name}</h1>
              <p className="text-text-muted text-sm uppercase font-black tracking-widest">By {product.vendor}</p>
            </div>

            <StockAvailabilityRow stock={product.stock} />

            <div className="flex items-baseline justify-center lg:justify-start gap-4 mb-10">
              <span className={`text-5xl font-serif ${isOutOfStock ? 'text-text-muted' : 'text-accent-navy'}`}>${finalPrice}</span>
              {product.originalPrice && <span className="text-xl text-text-muted line-through">${product.originalPrice}</span>}
            </div>

            <p className="text-lg text-text-main font-light leading-relaxed mb-10 italic border-l-4 border-accent-gold pl-6">
              "{product.description}"
            </p>

            {Object.keys(groupedVariations).length > 0 && (
              <div className="mb-10 space-y-6">
                {Object.entries(groupedVariations).map(([type, options]) => (
                  <div key={type}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">{type}</p>
                    <div className="flex flex-wrap gap-3">
                      {options.map(option => {
                        const isSelected = selectedVariations[type] === option.id;
                        const isOptionOutOfStock = option.stock_count <= 0;
                        return (
                          <button
                            key={option.id}
                            onClick={() => !isOptionOutOfStock && setSelectedVariations(previous => ({ ...previous, [type]: option.id }))}
                            disabled={isOptionOutOfStock}
                            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all border-2 ${
                              isSelected
                                ? 'bg-accent-navy border-accent-navy text-white shadow-md'
                                : isOptionOutOfStock
                                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-white border-brand-dark text-accent-navy hover:border-accent-gold'
                            }`}
                          >
                            {option.value}
                            {option.price_modifier > 0 && <span className="ml-2 text-[10px] opacity-80">(+${option.price_modifier})</span>}
                            {option.price_modifier < 0 && <span className="ml-2 text-[10px] opacity-80">(-${Math.abs(option.price_modifier)})</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeGroupBuys.length > 0 && !highlightedGb && (
              <div className="mb-10 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy">Community Campaigns</h3>
                  <div className="w-12 h-1 bg-accent-gold rounded-full opacity-30" />
                </div>
                <div className="space-y-4">
                  {activeGroupBuys.map(groupBuy => (
                    <div key={groupBuy.id} className="p-6 bg-white rounded-[32px] border-2 border-brand-dark hover:border-accent-gold/30 transition-all">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-brand-light text-accent-gold flex items-center justify-center">
                            <SafeIcon icon={FiUsers} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-accent-navy">{groupBuy.current} / {groupBuy.goal} Participants</p>
                            <p className="text-[10px] text-text-muted font-black uppercase tracking-widest flex items-center gap-2">
                              <SafeIcon icon={FiClock} className="text-accent-gold" /> {getTimeRemaining(groupBuy.deadline)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-1">Your Portion</p>
                          <p className="text-xl font-serif text-accent-navy">${calculatePortion(finalPrice, groupBuy.goal)}</p>
                        </div>
                      </div>
                      {groupBuy.description && (
                        <div className="mb-6 p-4 bg-brand-light rounded-2xl border border-brand-dark">
                          <p className="text-xs text-text-main italic">"{groupBuy.description}"</p>
                        </div>
                      )}
                      <div className="space-y-4">
                        <div className="h-2 bg-brand-light rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${calculateProgress(groupBuy.current, groupBuy.goal)}%` }} className="h-full bg-accent-gold" />
                        </div>
                        <button onClick={() => joinGroupBuy(groupBuy.id)}
                          className="w-full py-4 text-[10px] font-black uppercase tracking-widest rounded-2xl bg-accent-gold/10 text-accent-gold hover:bg-accent-gold/20 transition-all">
                          Join & Add Portion To Cart
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-10"><TrustBadges /></div>

            <div className="flex flex-col gap-4 mt-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                {isOutOfStock ? (
                  <button
                    onClick={handleNotifyMe}
                    className={`flex-grow py-5 text-lg flex items-center justify-center gap-3 shadow-xl rounded-2xl font-black uppercase tracking-widest transition-all ${
                      notifyDone
                        ? 'bg-green-500 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 border-2 border-zinc-200'
                    }`}
                  >
                    {notifyDone
                      ? <><SafeIcon icon={FiCheckCircle} /> You're on the list!</>
                      : <><SafeIcon icon={FiBell} /> Notify When Available</>}
                  </button>
                ) : (
                  <button onClick={handleAddToCart} disabled={isAdded}
                    className={`flex-grow py-5 text-lg flex items-center justify-center gap-3 shadow-xl rounded-2xl font-black uppercase tracking-widest transition-all ${
                      isAdded ? 'bg-green-500 text-white' : 'bg-[#D2F53E] text-accent-navy hover:bg-[#C0E42C] active:scale-95'
                    }`}>
                    {isAdded
                      ? <><SafeIcon icon={FiCheckCircle} /> Added to Cart</>
                      : <><SafeIcon icon={FiShoppingBag} /> Buy Individually</>}
                  </button>
                )}

                <div className="flex gap-4">
                  <button onClick={() => toggleWishlist(product)}
                    className={`p-5 rounded-2xl border-2 transition-all ${
                      isWishlisted ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-brand-dark text-accent-navy hover:border-accent-gold'
                    }`}>
                    <SafeIcon icon={FiHeart} className={isWishlisted ? 'fill-current' : ''} />
                  </button>
                  <button onClick={() => setIsShareOpen(true)}
                    className="p-5 bg-white border-2 border-brand-dark rounded-2xl text-accent-navy hover:border-accent-gold transition-all">
                    <SafeIcon icon={FiShare2} />
                  </button>
                </div>
              </div>

              {!activeGbId && !isOutOfStock && (
                <button onClick={() => setIsGroupBuyOpen(true)}
                  className="w-full py-5 bg-white border-2 border-dashed border-accent-gold/40 text-accent-gold rounded-3xl flex items-center justify-center gap-3 font-bold hover:bg-accent-gold/5 transition-all group">
                  <div className="w-8 h-8 rounded-lg bg-accent-gold/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <SafeIcon icon={FiUsers} />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-widest leading-none mb-1">Collaborative Tribute</p>
                    <p className="text-xs font-light text-text-muted">Start a Group Buy campaign</p>
                  </div>
                  <SafeIcon icon={FiPlus} className="ml-auto mr-4 opacity-40" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)}
        title={product.name} url={window.location.href} description={product.description} />
      <GroupBuyModal isOpen={isGroupBuyOpen} onClose={() => setIsGroupBuyOpen(false)}
        product={product} onConfirm={handleStartGroupBuy} />
    </div>
  );
};

export default ProductDetail;